import { useCallback, useEffect, useState } from 'react';
import { Check, Folder, Inbox, Loader2, X } from 'lucide-react';
import { MCP_CLIENT_LABELS, mcpClientOf } from '@repo/shared-types';
import { spaceErrorMessage, spacesApi, type SpaceShareRequest } from '../../lib/api';
import { formatDate, timeAgo } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const dateOnly = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

/** What the request would do, as a sentence about the person. */
function asks(request: SpaceShareRequest): string {
  const who = request.email ?? 'someone';
  if (request.access === 'none') return `Take ${who}'s access away`;
  const until = request.grantExpiresAt ? ` until ${dateOnly.format(new Date(request.grantExpiresAt))}` : '';
  if (request.currentAccess) return `Change ${who} from ${request.currentAccess} to ${request.access}${until}`;
  return `Share with ${who} as ${request.access}${until}`;
}

/** Which assistant asked, from its User-Agent — the same reading the Feed's source chips use. */
function askedBy(request: SpaceShareRequest): string {
  const client = mcpClientOf(request.userAgent);
  return client && client !== 'other' ? MCP_CLIENT_LABELS[client] : 'your MCP client';
}

/**
 * Share changes your MCP client asked for. An agent reads text other people wrote, so it can only ask:
 * nothing here has been applied, and nothing is until you approve it — which applies it exactly as a
 * share made from the folder's Share dialog would be.
 */
export function RequestsView({
  onDecided,
}: {
  /** A request was approved or declined: the count in the nav, and the decided folder's listing, are stale. */
  onDecided: (folderId: string | null) => void;
}) {
  const [rows, setRows] = useState<SpaceShareRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await spacesApi.shareRequests());
      setError(null);
    } catch (err) {
      setError(spaceErrorMessage(err, 'Could not load your share requests'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(request: SpaceShareRequest, approve: boolean) {
    setBusy(request.id);
    setError(null);
    try {
      if (approve) await spacesApi.approveShareRequest(request.id);
      else await spacesApi.declineShareRequest(request.id);
      setRows((prev) => prev?.filter((r) => r.id !== request.id) ?? null);
      onDecided(approve ? request.folderId : null);
    } catch (err) {
      setError(spaceErrorMessage(err, approve ? 'Could not apply it' : 'Could not decline it'));
      // Decided elsewhere, expired, or refused now (the person's account was suspended since): re-read.
      void load();
      onDecided(null);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <h1 className="text-lg font-bold text-ink flex items-center gap-2">
        <Inbox size={16} className="text-ink-faint" />
        Share requests
      </h1>
      <p className="mt-1 mb-5 max-w-2xl text-xs text-ink-muted">
        Changes to who can see your folders that your MCP client asked for. Nothing here has been applied: an assistant can ask to
        share, but only you can approve it. A request you leave expires after a week.
      </p>

      {error && <div className="mb-4 rounded-md border border-danger-ring bg-danger-bg px-4 py-3 text-sm text-danger-text">{error}</div>}

      {rows === null ? (
        <p className="text-xs text-ink-faint">{error ? '' : 'Loading…'}</p>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-border bg-card py-16 text-center text-ink-faint">
          <Inbox size={20} className="mx-auto mb-2 opacity-60" />
          <p className="text-sm">No share requests are waiting.</p>
        </div>
      ) : (
        <ul className="rounded-lg border border-border bg-card divide-y divide-border-subtle">
          {rows.map((request) => (
            <li key={request.id} className="flex items-center gap-3 px-4 py-3">
              <Folder size={14} className="text-orange-500 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-ink truncate">
                  {asks(request)}
                  {request.access === 'editor' && (
                    <Badge variant="warning" className="ml-2 px-1.5 py-0 text-[10px]">
                      can change files
                    </Badge>
                  )}
                </p>
                <p className="text-[11px] text-ink-faint truncate">
                  <span className="font-mono">{request.folderAddress}</span>
                  {' · '}
                  asked by {askedBy(request)} <span title={formatDate(request.createdAt)}>{timeAgo(request.createdAt)}</span>
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => void decide(request, false)} disabled={busy !== null}>
                <X size={12} />
                Decline
              </Button>
              <Button size="sm" onClick={() => void decide(request, true)} disabled={busy !== null}>
                {busy === request.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                Approve
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
